import type { O2dTab } from '../interfaces/tab.interface.js';

/**
 * Resolves the effective active tab id for `<o2d-tab-list>` from the two
 * selection sources, following the controlled-with-default precedence:
 *
 *   1. `activeId`      — the consumer-controlled prop (wins if it resolves to an
 *                        ENABLED tab), so external selection overrides local state.
 *   2. `localActiveId` — the element's internal selection (from click/keyboard),
 *                        used when `activeId` is unset/unknown/points at a disabled tab.
 *   3. first enabled   — the default when neither id resolves to an enabled tab.
 *   4. `undefined`     — degenerate: no enabled tab exists (empty or all-disabled),
 *                        meaning "no tab selected".
 *
 * Each candidate id is only honoured when it maps to an ENABLED tab; a disabled
 * or unknown id is skipped to the next precedence level. Pure function — no DOM,
 * no side effects — so it is unit-testable in isolation.
 *
 * @param tabs          The current tab configuration.
 * @param activeId      Consumer-controlled active id (may be undefined).
 * @param localActiveId Element-internal active id (may be undefined).
 * @returns The effective active tab id, or `undefined` when nothing is selectable.
 */
export function resolveEffectiveActiveId(
    tabs: readonly O2dTab[],
    activeId: string | undefined,
    localActiveId: string | undefined,
): string | undefined {
    if (isEnabled(tabs, activeId)) {
        return activeId;
    }
    if (isEnabled(tabs, localActiveId)) {
        return localActiveId;
    }
    return tabs.find((tab) => !tab.disabled)?.id;
}

/**
 * True when `id` is defined and matches a tab that is present and not disabled.
 */
function isEnabled(tabs: readonly O2dTab[], id: string | undefined): id is string {
    return id !== undefined && tabs.some((tab) => tab.id === id && !tab.disabled);
}
